import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { notification, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { OpCard } from "components/customAntd/DLS/OpCard/OpCard";
import { OpModal } from "components/customAntd/DLS/OpModal/OpModal";
import { VideoDeviceType, DevicesByWorkflowType } from "types/videoDeviceTypes";
import {
  botLoginAltaVideo,
  botLogoutAltaVideo,
} from "store/slices/altaVideoSlice";
import { AvaConnection, generateUniqueLabel } from "ava-player/src";
import { useTranslation } from "react-i18next";
import { DATE_TIME_AM_PM_FORMAT } from "constants/dates";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ActivityLogVideoModalProps {
  open: boolean;
  onClose: () => void;
  targetType?: "SIGNIN" | "SIGNOUT";
  videoDevices: VideoDeviceType[];
  devicesByWorkflow: DevicesByWorkflowType;
  selectedDeviceGuIds: string[];
}

const IS_LOCAL = window.location.hostname === "localhost";

const ActivityLogVideoModal: React.FC<ActivityLogVideoModalProps> = ({
  open,
  onClose,
  targetType,
  videoDevices,
  devicesByWorkflow,
  selectedDeviceGuIds,
}) => {
  const dispatch: AppDispatch = useDispatch();

  const { selectedVisitor } = useSelector((state: RootState) => state.visits);
  const { altaVideoIntegrationData } = useSelector(
    (state: RootState) => state.altaVideo,
  );

  const [selectedDevice, setSelectedDevice] = useState<VideoDeviceType | null>(
    null,
  );
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>(
    {},
  );
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);

  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const connRef = useRef<AvaConnection | null>(null);

  const HOST = useMemo(() => {
    if (IS_LOCAL) return "localhost:4000";
    return altaVideoIntegrationData?.[0]?.msiAltaVideoApiHost || "";
  }, [altaVideoIntegrationData]);

  const BASE_URL = useMemo(() => {
    if (!HOST) return "";
    const protocol = IS_LOCAL ? "http" : "https";
    return `${protocol}://${HOST}`;
  }, [HOST]);

  const timezone = useMemo(
    () =>
      devicesByWorkflow?.site?.timezone
        ? devicesByWorkflow.site.timezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone, // Use system location timezone if timezone is null
    [devicesByWorkflow?.site?.timezone],
  );

  const baseUtc = useMemo(() => {
    const when =
      targetType === "SIGNOUT"
        ? selectedVisitor?.signOut
        : selectedVisitor?.signIn;
    return dayjs.tz(when, timezone).utc(); // Convert to UTC
  }, [timezone, targetType, selectedVisitor?.signIn, selectedVisitor?.signOut]);

  // Clear connection
  const teardown = () => {
    try {
      connRef.current?.teardown();
    } catch {}
    connRef.current = null;
  };

  // Loads thumbnails
  const fetchThumbnails = useCallback(async () => {
    const filteredDevices = videoDevices.filter((d) =>
      selectedDeviceGuIds.includes(d.guId),
    );
    const result: Record<string, string> = {};

    await Promise.all(
      filteredDevices.map(async (device) => {
        if (device.errors?.length) return;

        try {
          const thumbnail = await axios.get(
            `${BASE_URL}/api/v1/devices/${device.guId}/sources/${device.sourceId[0]}/thumbnail`,
            {
              params: {
                time: baseUtc.valueOf(),
              },
              withCredentials: true,
              responseType: "blob",
            },
          );
          result[device.guId] = URL.createObjectURL(thumbnail.data);
        } catch {
          // go to next device
        }
      }),
    );
    setThumbnailUrls(result);
    setIsThumbnailLoading(false);

    // Once thumbnail loading is completed, automatically select first available video device
    const firstValidDevice = filteredDevices.find(
      (device) => !device.errors?.length && result[device.guId],
    );
    if (firstValidDevice) {
      setSelectedDevice(firstValidDevice);
    }
  }, [BASE_URL, videoDevices, selectedDeviceGuIds, baseUtc]);

  // Start play (based on the selected device)
  const start = useCallback(async () => {
    if (!selectedDevice) return;
    teardown();

    const element = videoRef.current;
    if (!element) return;

    const label = generateUniqueLabel();

    try {
      // Bot Login
      await dispatch(botLoginAltaVideo());

      // TURN
      const { data: turnConfig } = await axios.get(
        `${BASE_URL}/api/v1/cloud/turn`,
        { withCredentials: true },
      );

      const ava = new AvaConnection(label, {
        turnConfig,
        videoElement: element,
        onPlaybackError: () => {
          element.pause();
          element.srcObject = null;
          teardown();
          start();
        },
      });

      connRef.current = ava;

      // WEBSOCKET
      const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${wsProtocol}://${HOST}/api/v1/devices/${selectedDevice.guId}/dynamicWebrtc`;
      await ava.bootstrap(wsUrl);

      // Play range (±10 seconds)
      const videoStartTime = new Date(baseUtc.valueOf() - 10_000);
      const videoEndTime = new Date(baseUtc.valueOf() + 10_000);

      await ava.startPlayback({
        label,
        deviceId: selectedDevice.guId,
        nodeId: selectedDevice.nodeId,
        startTimestamp: videoStartTime,
        endTimestamp: videoEndTime,
      });
    } catch (error) {
      teardown();
      notification.error({
        message: t("notification.error"),
        description: t("visitors.profile.notification.unexpectedVideoError"),
        placement: "bottomRight",
      });
    } finally {
      // Bot Logout
      dispatch(botLogoutAltaVideo());
    }
  }, [BASE_URL, HOST, selectedDevice, baseUtc, t, dispatch]);

  // Modal open/close: refresh -> bot log in -> load thumbnail
  useEffect(() => {
    if (!open || !selectedVisitor?.id) return;

    teardown();
    setIsThumbnailLoading(true);
    setThumbnailUrls({});
    setSelectedDevice(null);

    dispatch(botLoginAltaVideo())
      .unwrap()
      .then(() => {
        fetchThumbnails();
      })
      .catch(() => {
        notification.error({
          message: t("notification.error"),
          description: t("visitors.profile.notification.unexpectedVideoError"),
        });
      });
  }, [BASE_URL, open, selectedVisitor?.id, dispatch, fetchThumbnails, t]);

  // Play when video device is selected
  useEffect(() => {
    if (selectedDevice) {
      start();
    }
  }, [selectedDevice, start]);

  // Device Card UI
  const DeviceCard = ({ device }: { device: VideoDeviceType }) => {
    const imageUrl = thumbnailUrls[device.guId] || "";
    const hasVideoError = device.errors?.length > 0;

    return (
      <OpCard
        style={{
          marginBottom: 24,
          cursor: hasVideoError || !imageUrl ? "not-allowed" : "pointer",
        }}
        onClick={() => !hasVideoError && imageUrl && setSelectedDevice(device)}
      >
        <div>
          <div
            style={{ fontWeight: 600, textAlign: "center", paddingBottom: 10 }}
          >
            {device.name}
          </div>
          {hasVideoError || !imageUrl ? (
            <div
              style={{
                width: "101%",
                height: 85,
                display: "flex",
                objectFit: "contain",
                background: "#999",
                borderRadius: 6,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {t("visitors.profile.videoLog.modal.noVideo")}
            </div>
          ) : (
            <img
              src={imageUrl}
              alt="thumbnail"
              style={{
                width: "101%",
                height: 85,
                objectFit: "contain",
                background: "#000",
                borderRadius: 6,
              }}
            />
          )}
        </div>
      </OpCard>
    );
  };

  return (
    <OpModal
      open={open}
      onCancel={() => {
        setSelectedDevice(null);
        onClose();
      }}
      footer={null}
      width={925}
      destroyOnClose
      title={
        <div style={{ display: "flex", alignItems: "baseline" }}>
          {t("location.tabs.video")}
          <span style={{ marginLeft: "8px", fontSize: "13px" }}>
            ({baseUtc.tz(timezone).format(`${DATE_TIME_AM_PM_FORMAT}`)})
          </span>
        </div>
      }
    >
      {isThumbnailLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 375,
          }}
        >
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      ) : (
        // Video & Thumbnail Cards
        <div style={{ display: "flex", maxHeight: "80vh", overflow: "hidden" }}>
          <div style={{ flex: 1 }}>
            {selectedDevice ? (
              <video
                ref={videoRef}
                muted
                playsInline
                controls
                style={{
                  width: "100%",
                  height: 370,
                  background: "#000000",
                  borderRadius: 15,
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: 375,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {t("visitors.profile.videoLog.modal.selectVideo")}
              </div>
            )}
          </div>

          <div
            style={{
              width: 200,
              paddingLeft: 20,
              overflowY: "auto",
              maxHeight: "370px",
            }}
          >
            {selectedVisitor?.signIn || selectedVisitor?.signOut ? (
              videoDevices
                .filter((d) => selectedDeviceGuIds.includes(d.guId)) // Filter by selected devices by workflow
                .map((d) => <DeviceCard key={d.guId} device={d} />)
            ) : (
              <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                NOT AVAILABLE
              </div>
            )}
          </div>
        </div>
      )}
    </OpModal>
  );
};

export default ActivityLogVideoModal;
